/** Picks the หลังบ้านพาร์ทเนอร์ backend: the mock (dev / screenshots) or the real API (same origin + session).
 *  As api/posts/index.ts: the mock (./mock, marker "cf-mock-aff") is loaded only when asked for, through a dynamic
 *  import behind a check Vite folds at build time — production builds with VITE_USE_MOCK=false, so the branch and the
 *  mock chunk are dropped (`npm run check` greps dist for the marker). */
import { useMock } from '../index'
import { HttpAffiliateApi } from './http'
import type { AffiliateApi } from './types'

let current: AffiliateApi | null = null

/** a grey box until the mock is loaded (picture and file URLs are synchronous) */
const PENDING_PICTURE =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22%3E%3Crect width=%2232%22 height=%2232%22 fill=%22%23eef2f0%22/%3E%3C/svg%3E'

/** the AffiliateApi methods that answer a URL at once (not a promise) */
const URL_HELPERS = new Set(['photoUrl', 'qrUrl', 'slipUrl', 'documentUrl', 'whtPdfUrl', 'monthWhtUrl', 'exportCsvUrl'])

/** Every async AffiliateApi call loads the mock on first use, then goes straight to it; the URL helpers stay sync. */
function lazyMock(): AffiliateApi {
  let real: AffiliateApi | null = null
  let loading: Promise<AffiliateApi> | null = null
  const load = () => (loading ??= import('./mock').then((m) => (real = new m.MockAffiliateApi({ delayMs: 250 }))))
  return new Proxy({} as AffiliateApi, {
    get: (_t, name: string) => {
      if (URL_HELPERS.has(name)) {
        return (arg: never) => (real ? (real[name as keyof AffiliateApi] as (a: never) => string)(arg) : PENDING_PICTURE)
      }
      return (...args: unknown[]) =>
        load().then((api) => (api[name as keyof AffiliateApi] as (...a: unknown[]) => Promise<unknown>).apply(api, args))
    },
  })
}

export function getAffiliateApi(): AffiliateApi {
  if (!current) {
    // `!== 'false'` on the raw env value is what Vite can fold: "false" in production → the mock branch is dead code
    current = import.meta.env.VITE_USE_MOCK !== 'false' && useMock
      ? lazyMock()
      : new HttpAffiliateApi((import.meta.env.VITE_API_BASE ?? '').trim())
  }
  return current
}

/** Tests inject their own backend. */
export function setAffiliateApi(api: AffiliateApi | null): void {
  current = api
}

export type * from './types'
